import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"
import type { CartItemInterface } from "@/models/cart-item"
import type {
  AddCartLineInput,
  CartLine,
  CartStoreSnapshot,
  CartStoreState,
  RemoveOneUnitInput,
  UpdateLineQuantityInput,
  UpdateModifierQuantityInput,
} from "@/store/cart/cart.types"
import { canonicalizeModifierGroups, isSameCartLine } from "@/store/cart/cart-line-identity"
import { legacyCartItemToCartLine, newClientItemId } from "@/store/cart/cart-line-adapters"
import { recalcCartLineTotals } from "@/store/cart/cart-line-totals"
import { buildCartLineValidityCapture } from "@/domain/cart/cart-line-capture"
import { getLatestMenuCatalogSnapshot } from "@/domain/cart/menu-catalog.registry"
import { sumMenuItemQuantity } from "@/domain/cart/cart.validation"
import { MAX_MENU_ITEM_QUANTITY } from "@/domain/cart/cart.constants"
import {
  sanitizePersistedCartLine,
  sanitizePersistedCartLines,
  type LegacyPersistedCartLine,
} from "@/store/cart/cart-line-sanitize"
import {
  hasActivePaymentSession,
  readPaymentStorefrontBase,
} from "@/domain/payment/payment-await-session"
import {
  readPaymentLockStorefront,
  shouldPreservePaymentCartLock,
} from "@/domain/payment/payment-lock-ownership"
import {
  CART_PERSIST_VERSION,
  LEGACY_CART_PERSIST_KEY,
  areCartLinesSafeForTenant,
  beginCartIsolationPending,
  cartPersistKey,
  completeCartIsolation,
  getIsolatedRestaurantId,
  isCartPersistWriteEnabled,
  migrateLegacyCartBlob,
  normalizeCartTenantId,
  serializePersistedCartBlob,
} from "@/store/cart/cart-tenant-isolation"

/** @deprecated Unscoped key — use {@link cartPersistKey} after tenant isolation. */
export const CART_PERSIST_KEY = LEGACY_CART_PERSIST_KEY

function currentStorefrontBase(): string {
  if (typeof window === "undefined") return ""
  const match = window.location.pathname.match(/^\/r\/([^/]+)(?:\/|$)/)
  return match ? `/r/${match[1]}` : ""
}

/** Payment/lock guards apply only when the session/lock belongs to this storefront. */
function paymentLockAppliesToCurrentStorefront(): boolean {
  const current = currentStorefrontBase()
  if (hasActivePaymentSession()) {
    return readPaymentStorefrontBase() === current
  }
  const lockBase = readPaymentLockStorefront()
  if (lockBase != null) {
    return lockBase === current
  }
  return true
}

function shouldBlockPaymentCartUnlock(): boolean {
  if (!paymentLockAppliesToCurrentStorefront()) return false
  return hasActivePaymentSession() || shouldPreservePaymentCartLock()
}

function tenantRejectsMutation(restaurantId: string): boolean {
  const isolated = getIsolatedRestaurantId()
  if (!isolated) return false
  return normalizeCartTenantId(restaurantId) !== isolated
}

function canMutate(state: CartStoreState): boolean {
  return !state.isLocked && !state.isCheckoutLocked
}

function attachValidityCapture(line: CartLine, menuItemId: string): CartLine {
  const snapshot = getLatestMenuCatalogSnapshot()
  const catalogItem = snapshot?.items.find(
    (item) => String(item.menu_item_id) === String(menuItemId)
  )
  const capture = buildCartLineValidityCapture({
    item_price: line.item_price,
    menu_version: catalogItem?.menu_version ?? 0,
    catalog_fingerprint: snapshot?.fingerprint ?? "",
    selected_modifiers: line.selected_modifiers,
  })
  return { ...line, validityCapture: capture }
}

function createLineFromAdd(
  input: AddCartLineInput,
  quantity: number,
  options?: { capture?: boolean }
): CartLine {
  const groups = canonicalizeModifierGroups(input.selected_modifiers ?? [])
  const draft: CartLine = {
    client_item_id: newClientItemId(),
    menu_item_id: input.menu_item_id,
    quantity,
    selected_modifiers: groups,
    item_name: input.item_name,
    item_price: input.item_price,
    category: input.category,
    restaurant_id: String(input.restaurant_id),
    selectedCombos: input.selectedCombos,
    comboTotalPerUnit: input.comboTotalPerUnit,
  }
  const line = recalcCartLineTotals(draft)
  if (options?.capture) {
    return attachValidityCapture(line, input.menu_item_id)
  }
  return line
}

function tryMigrateLegacyReduxCart(): CartLine[] | null {
  try {
    const raw = localStorage.getItem("persist:root")
    if (!raw) return null
    const root = JSON.parse(raw) as { cart?: string }
    if (!root.cart) return null
    const cartState = JSON.parse(root.cart) as { items?: CartItemInterface[] }
    if (!cartState.items?.length) return null
    return cartState.items
      .filter((i) => i != null && (i.count ?? 0) >= 1)
      .map((i) => recalcCartLineTotals(legacyCartItemToCartLine(i)))
  } catch {
    return null
  }
}

type CartStoreActions = {
  addItem: (input: AddCartLineInput) => void
  removeOneUnit: (input: RemoveOneUnitInput) => void
  updateItemQuantity: (input: UpdateLineQuantityInput) => void
  updateModifierQuantity: (input: UpdateModifierQuantityInput) => void
  removeLineByClientId: (client_item_id: string) => void
  clearCart: (options?: { force?: boolean }) => void
  lockCart: () => void
  unlockCart: () => void
  setCheckoutLocked: (locked: boolean) => void
  replaceItems: (items: CartLine[]) => void
}

export type CartStore = CartStoreState & CartStoreActions

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isLocked: false,
      isCheckoutLocked: false,

      addItem: (input) => {
        const state = get()
        if (!canMutate(state)) return
        if (tenantRejectsMutation(String(input.restaurant_id))) return
        const currentAggregate = sumMenuItemQuantity(state.items, input.menu_item_id)
        if (currentAggregate + 1 > MAX_MENU_ITEM_QUANTITY) return
        const mods = canonicalizeModifierGroups(input.selected_modifiers ?? [])
        const newLine = createLineFromAdd({ ...input, selected_modifiers: mods }, 1, { capture: true })
        const idx = state.items.findIndex((el) => isSameCartLine(el, newLine))
        if (idx >= 0) {
          const cur = state.items[idx]
          const merged = recalcCartLineTotals({
            ...cur,
            quantity: cur.quantity + 1,
          })
          set({
            items: state.items.map((it, i) => (i === idx ? merged : it)),
          })
        } else {
          set({ items: [...state.items, newLine] })
        }
      },

      removeOneUnit: (input) => {
        const state = get()
        if (!canMutate(state)) return
        const candidates = state.items
          .map((el, i) => ({ el, i }))
          .filter(({ el }) => String(el.menu_item_id) === String(input.menu_item_id))
        let idx = -1
        if (candidates.length === 1) {
          idx = candidates[0].i
        } else if (candidates.length > 1) {
          const hasIdentity =
            (input.selected_modifiers != null && input.selected_modifiers.length > 0) ||
            (input.selectedCombos != null && input.selectedCombos.length > 0)
          if (hasIdentity) {
            const probe = createLineFromAdd(
              {
                menu_item_id: input.menu_item_id,
                item_name: "",
                item_price: 0,
                category: "",
                restaurant_id: "",
                selected_modifiers: input.selected_modifiers,
                selectedCombos: input.selectedCombos,
              },
              1
            )
            const found = candidates.find(({ el }) => isSameCartLine(el, probe))
            if (found) idx = found.i
          }
          if (idx < 0) idx = candidates[0].i
        }
        if (idx < 0) return
        const item = state.items[idx]
        const nextQty = item.quantity - 1
        if (nextQty <= 0) {
          set({ items: state.items.filter((_, i) => i !== idx) })
        } else {
          const merged = recalcCartLineTotals({ ...item, quantity: nextQty })
          set({ items: state.items.map((it, i) => (i === idx ? merged : it)) })
        }
      },

      updateItemQuantity: (input) => {
        const state = get()
        if (!canMutate(state)) return
        const payloadMods = canonicalizeModifierGroups(input.selected_modifiers)
        const candidates = state.items
          .map((el, i) => ({ el, i }))
          .filter(({ el }) => String(el.menu_item_id) === String(input.menu_item_id))
        let idx = -1
        if (candidates.length === 1) {
          idx = candidates[0].i
        } else if (candidates.length > 1 && (payloadMods.length > 0 || input.selectedCombos != null)) {
          const probe = createLineFromAdd(
            {
              menu_item_id: input.menu_item_id,
              item_name: "",
              item_price: 0,
              category: "",
              restaurant_id: "",
              selected_modifiers: payloadMods,
              selectedCombos: input.selectedCombos,
              comboTotalPerUnit: input.comboTotalPerUnit,
            },
            1
          )
          const found = candidates.find(({ el }) => isSameCartLine(el, probe))
          if (found) idx = found.i
        }
        if (idx < 0 && candidates.length > 0) idx = candidates[0].i
        if (idx < 0) return

        const qty = Math.max(0, Math.floor(input.quantity))
        if (qty === 0) {
          set({ items: state.items.filter((_, i) => i !== idx) })
          return
        }
        const otherLinesAggregate = sumMenuItemQuantity(
          state.items.filter((_, i) => i !== idx),
          input.menu_item_id
        )
        if (otherLinesAggregate + qty > MAX_MENU_ITEM_QUANTITY) return
        const item = state.items[idx]
        let next: CartLine = { ...item, quantity: qty }
        if (payloadMods.length > 0) {
          next = { ...next, selected_modifiers: payloadMods }
        }
        if (input.comboTotalPerUnit !== undefined) {
          next = { ...next, comboTotalPerUnit: input.comboTotalPerUnit }
        }
        if (input.selectedCombos) {
          next = { ...next, selectedCombos: input.selectedCombos }
        }
        set({ items: state.items.map((it, i) => (i === idx ? recalcCartLineTotals(next) : it)) })
      },

      updateModifierQuantity: (input) => {
        const state = get()
        if (!canMutate(state)) return
        set({
          items: state.items.map((line) => {
            if (line.client_item_id !== input.client_item_id) return line
            const groups = line.selected_modifiers.map((g) => {
              if (g.modifier_group_id !== input.modifier_group_id) return g
              return {
                ...g,
                items: g.items.map((m) =>
                  m.modifier_group_item_id === input.modifier_group_item_id
                    ? { ...m, quantity: input.quantity }
                    : m
                ),
              }
            })
            return recalcCartLineTotals({ ...line, selected_modifiers: groups })
          }),
        })
      },

      removeLineByClientId: (client_item_id) => {
        const state = get()
        if (!canMutate(state)) return
        set({ items: state.items.filter((l) => l.client_item_id !== client_item_id) })
      },

      clearCart: (options) => {
        if (!options?.force && shouldBlockPaymentCartUnlock()) return
        set({
          items: [],
          isLocked: false,
          isCheckoutLocked: false,
        })
      },

      lockCart: () => set({ isLocked: true }),
      unlockCart: () => {
        if (shouldBlockPaymentCartUnlock()) return
        set({ isLocked: false })
      },
      setCheckoutLocked: (locked) => {
        if (!locked && shouldBlockPaymentCartUnlock()) return
        set({ isCheckoutLocked: locked })
      },

      replaceItems: (items) => {
        const state = get()
        if (!canMutate(state)) return
        const next = items
          .filter((i) => i != null && (i.quantity ?? 0) >= 1)
          .map((i) => recalcCartLineTotals(sanitizePersistedCartLine(i)))
        const isolated = getIsolatedRestaurantId()
        if (isolated && !areCartLinesSafeForTenant(next, isolated)) return
        set({ items: next })
      },
    }),
    {
      name: LEGACY_CART_PERSIST_KEY,
      skipHydration: true,
      storage: createJSONStorage(() => ({
        getItem: (name: string) => {
          try {
            return localStorage.getItem(name)
          } catch {
            return null
          }
        },
        setItem: (name: string, value: string) => {
          if (!isCartPersistWriteEnabled()) return
          localStorage.setItem(name, value)
        },
        removeItem: (name: string) => {
          if (!isCartPersistWriteEnabled()) return
          localStorage.removeItem(name)
        },
      })),
      partialize: (s): CartStoreSnapshot => ({
        items: s.items,
        isLocked: s.isLocked,
        isCheckoutLocked: s.isCheckoutLocked,
      }),
      version: CART_PERSIST_VERSION,
      migrate: (persistedState, version) => {
        const state = persistedState as CartStoreSnapshot & {
          items?: LegacyPersistedCartLine[]
        }
        // v2: strip deprecated `stale`. v3: remint non-UUID client_item_id (Guid wire).
        if (version < 3 && Array.isArray(state?.items)) {
          state.items = sanitizePersistedCartLines(state.items)
        }
        return state as CartStoreSnapshot
      },
      onRehydrateStorage: () => (state) => {
        if (state?.items?.length) {
          state.items = sanitizePersistedCartLines(state.items as LegacyPersistedCartLine[])
        }
        if (state && (!state.items || state.items.length === 0)) {
          const migrated = tryMigrateLegacyReduxCart()
          if (migrated?.length) {
            state.items = migrated
          }
        }
      },
    }
  )
)

export function getCartSnapshot(): CartStoreSnapshot {
  const s = useCartStore.getState()
  return {
    items: s.items,
    isLocked: s.isLocked,
    isCheckoutLocked: s.isCheckoutLocked,
  }
}

const EMPTY_CART_SNAPSHOT: CartStoreSnapshot = {
  items: [],
  isLocked: false,
  isCheckoutLocked: false,
}

let hydrateGeneration = 0

function applyEmptyCartMemory(): void {
  useCartStore.setState({ ...EMPTY_CART_SNAPSHOT })
}

/**
 * After tenant resolution: migrate legacy `gmf-cart-v1`, bind persist to
 * `gmf-cart-v1:{restaurantId}`, rehydrate. Must complete before cart UI mounts.
 */
export async function isolateAndHydrateCart(restaurantId: string): Promise<void> {
  const gen = ++hydrateGeneration
  const rid = normalizeCartTenantId(restaurantId)

  beginCartIsolationPending()
  applyEmptyCartMemory()

  if (!rid) {
    return
  }

  try {
    migrateLegacyCartBlob(localStorage, rid)
  } catch {
    /* private mode / parse errors — continue fail-closed */
  }

  const persistKey = cartPersistKey(rid)
  useCartStore.persist.setOptions({ name: persistKey })

  try {
    await useCartStore.persist.rehydrate()
  } catch {
    if (gen !== hydrateGeneration) return
    applyEmptyCartMemory()
    completeCartIsolation(rid, persistKey)
    return
  }

  if (gen !== hydrateGeneration) return

  const hydrated = useCartStore.getState()
  if (!areCartLinesSafeForTenant(hydrated.items, rid)) {
    applyEmptyCartMemory()
    try {
      localStorage.setItem(persistKey, serializePersistedCartBlob(EMPTY_CART_SNAPSHOT))
    } catch {
      /* private mode */
    }
  }

  completeCartIsolation(rid, persistKey)
}
